import type { EngineConfig, PluginConfigEntry } from '@typings/plugin-runtime.ts';

import featureFlags from '../../deploy/previewFeatureFlags.json';

type UrlConfig = Pick<EngineConfig, 'debug'> & {
  plugins: Record<string, (PluginConfigEntry & { preservedQueryParams?: Record<string, string> }) | false>;
};

const parmToPlugin = {
  navFrom: 'online/getDirectionsFromTo',
  navTo: 'online/getDirectionsFromTo',
  waypoints: 'online/getDirectionsFromTo',
  accessible: 'online/getDirectionsFromTo',
  showNav: 'online/getDirectionsFromTo',
  poiId: ['online/getDirectionsFromTo', 'online/poiView'],
  vid: 'venueDataLoader',
  lang: 'venueDataLoader',
  stage: 'venueDataLoader',
  contentStage: 'venueDataLoader',
  accountId: 'venueDataLoader',
  search: 'online/headerOnline',
  ho: ['online/getDirectionsFromTo', 'analytics2'],
  home: 'online/homeView',
  zoom: 'mapRenderer',
  pitch: 'mapRenderer',
  bearing: 'mapRenderer',
  lat: 'mapRenderer',
  lng: 'mapRenderer',
  radius: 'mapRenderer',
  buildingId: 'mapRenderer',
  floorId: 'mapRenderer',
  visualTest: 'mapRenderer',
  refInstallId: 'analytics2',
  disableZoomToExplorePopup: 'levelIndicator',
} as const;

type ParmKey = keyof typeof parmToPlugin;

export function process<T extends UrlConfig>(config: T) {
  const parms = new URLSearchParams(location.search);

  // SDK opt-in deep links also run this processor; staging flags are only for MOL instances.
  // Staging assets are promoted to production unchanged, so gate on the page URL at runtime.
  if (
    config.plugins.sdkServer === undefined &&
    location.origin === 'https://apps.locuslabs.com' &&
    /^\/online\/(?!dist\/)[a-zA-Z0-9_-]+\/(?:index\.html)?$/.test(location.pathname)
  ) {
    for (const [flag, pluginId] of Object.entries(featureFlags)) {
      const value = parms.get(flag);
      if (value === null) {
        continue;
      }
      if (value !== 'true' && value !== 'false') {
        console.warn(`Ignoring invalid ${flag} staging override: expected "true" or "false".`);
        continue;
      }

      const pluginConfig = config.plugins[pluginId];
      if (!pluginConfig || pluginConfig.active === false) {
        console.warn(`Ignoring ${flag} staging override: ${pluginId} is disabled or absent.`);
        continue;
      }
      config.plugins = {
        ...config.plugins,
        [pluginId]: { ...pluginConfig, [flag]: value === 'true' },
      };

      const deepLinkingConfig = config.plugins.deepLinking;
      if (deepLinkingConfig && deepLinkingConfig.active !== false) {
        config.plugins.deepLinking = {
          ...deepLinkingConfig,
          preservedQueryParams: { ...deepLinkingConfig.preservedQueryParams, [flag]: value },
        };
      }
    }
  }

  return setDeepLinksForParms(config, parms);
}

// Note: see note about security for forceCreate on postproc-stateTracking - same thing
// applies here.
export function setDeepLinksForParms<T extends UrlConfig>(config: T, parms: URLSearchParams, forceCreate = false) {
  if (parms.has('lldebug')) {
    try {
      const parsedDebug = JSON.parse(parms.get('lldebug') ?? 'null') as boolean | Record<string, unknown> | null;
      if (parsedDebug === null) {
        config.debug = {};
      } else {
        config.debug = parsedDebug;
      }
    } catch {
      config.debug = true;
    }
  }

  (Object.keys(parmToPlugin) as ParmKey[]).forEach(key => {
    if (!parms.has(key)) {
      return;
    }

    const pluginNames = parmToPlugin[key];
    const plugins = Array.isArray(pluginNames) ? pluginNames : [pluginNames];

    plugins.forEach(plugin => {
      let pluginConfig = config.plugins[plugin];
      if (!pluginConfig && forceCreate) {
        pluginConfig = config.plugins[plugin] = {};
      }
      if (!pluginConfig) {
        return;
      }

      pluginConfig.deepLinkProps = { ...pluginConfig.deepLinkProps, [key]: parms.get(key) };
    });
  });

  const poiViewConfig = config.plugins['online/poiView'];
  if (parms.has('poiId') && parms.has('showNav') && poiViewConfig) {
    delete poiViewConfig.deepLinkProps?.poiId;
  }

  return config;
}
